import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import cls from "./MemberProfileEditMember.module.scss";
import api from "@/utils/axios";
import { toast } from "react-toastify";

import {
  EXPERIENCE_LEVELS,
  SECONDARY_LANGUAGES,
  MEMBER_TYPES,
} from "@/enums/MemberEnums";

const MemberProfileEditMember = () => {
  const { t } = useTranslation(); // common namespace assumed
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [member, setMember] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);

  const [experienceLevel, setExperienceLevel] = useState("");
  const [secondaryLanguage, setSecondaryLanguage] = useState("");
  const [memberType, setMemberType] = useState("");
  const [memberRenewDay, setMemberRenewDay] = useState("");

  /* ---------------------- Label helpers (same as admin) --------------------- */

  const formatLabel = (key) => {
    if (!key) return "";
    return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const languageLabel = (code) => {
    const map = {
      en: "English",
      si: "සිංහල (Sinhala)",
      ta: "தமிழ் (Tamil)",
      hi: "हिन्दी (Hindi)",
    };
    return map[code] || code.toUpperCase();
  };

  const typeLabel = (tVal) => {
    if (tVal === "single") return "Single";
    if (tVal === "couple") return "Couple";
    return formatLabel(tVal);
  };

  const avatarUrl = useMemo(() => {
    if (avatarPreview) return avatarPreview;
    if (member?.avatar?.url) return member.avatar.url;
    return null;
  }, [avatarPreview, member]);

  /* ---------------------------- Load member info ---------------------------- */

  const loadMember = async () => {
    try {
      setLoading(true);
      const res = await api.get("/v1/member/profile/member-info");
      const data = res.data?.member;
      setMember(data || null);

      if (data) {
        setExperienceLevel(data.experienceLevel || "");
        setSecondaryLanguage(data.secondaryLanguage || "");
        setMemberType(data.memberType || "");

        if (data.member_renew_day) {
          const d = new Date(data.member_renew_day);
          if (!Number.isNaN(d.getTime())) {
            setMemberRenewDay(d.toISOString().slice(0, 10));
          }
        } else {
          setMemberRenewDay("");
        }
      }

      setAvatarPreview(null);
      setAvatarFile(null);
    } catch (err) {
      console.error(err);
      toast.error(
        t("memberProfileEditMember.messages.loadError", {
          defaultValue: "Failed to load your member info",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMember();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ----------------------------- Save member info --------------------------- */

  const handleSave = async (e) => {
    e.preventDefault();
    if (!member) return;

    try {
      setSaving(true);

      await api.patch("/v1/member/profile/member-info", {
        experienceLevel: experienceLevel || undefined,
        secondaryLanguage: secondaryLanguage || undefined,
        // status, memberType, origin, member_renew_day are admin-only
      });

      toast.success(
        t("memberProfileEditMember.messages.updateSuccess", {
          defaultValue: "Your member details have been updated",
        }),
      );
      await loadMember();
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        t("memberProfileEditMember.messages.updateError", {
          defaultValue: "Failed to update your member info",
        });
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  /* ----------------------------- Avatar handlers ---------------------------- */

  const handleAvatarFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setAvatarFile(null);
      setAvatarPreview(null);
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error(
        t("memberProfileEditMember.messages.avatarSelectError", {
          defaultValue: "Please select an image file",
        }),
      );
      return;
    }

    setAvatarFile(file);
    const url = URL.createObjectURL(file);
    setAvatarPreview(url);
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile) {
      toast.error(
        t("memberProfileEditMember.messages.avatarMissing", {
          defaultValue: "Please choose an image first",
        }),
      );
      return;
    }

    try {
      setUploading(true);

      const file = avatarFile;
      const ext =
        file.name && file.name.includes(".")
          ? file.name.split(".").pop().toLowerCase()
          : "webp";
      const contentType = file.type || "image/webp";

      // 1) Request presigned URL from member-side endpoint
      const res = await api.post("/v1/member/profile/avatar/presign", {
        ext,
        contentType,
      });

      const { uploadUrl } = res.data || {};
      if (!uploadUrl) {
        toast.error(
          t("memberProfileEditMember.messages.avatarPresignError", {
            defaultValue: "Could not get an upload URL",
          }),
        );
        return;
      }

      // 2) Upload directly to S3
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": contentType,
        },
        body: file,
      });

      if (!putRes.ok) {
        console.error("S3 upload failed", putRes.status, await putRes.text());
        toast.error(
          t("memberProfileEditMember.messages.avatarUploadError", {
            defaultValue: "Failed to upload avatar",
          }),
        );
        return;
      }

      toast.success(
        t("memberProfileEditMember.messages.avatarUploadSuccess", {
          defaultValue: "Avatar uploaded successfully",
        }),
      );

      setAvatarFile(null);
      setAvatarPreview(null);

      // 3) Refresh member data to get new signed avatar URL
      await loadMember();
    } catch (err) {
      console.error(err);
      toast.error(
        t("memberProfileEditMember.messages.avatarUploadError", {
          defaultValue: "Failed to upload avatar",
        }),
      );
    } finally {
      setUploading(false);
    }
  };

  /* --------------------------------- Back ---------------------------------- */

  const handleBack = () => {
    navigate(-1);
  };

  /* ------------------------------- Loading UI ------------------------------- */

  if (loading && !member) {
    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← {t("memberProfileEditMember.back", { defaultValue: "Back" })}
          </button>
          <h1 className={cls.title}>
            {t("memberProfileEditMember.title", {
              defaultValue: "Member core details",
            })}
          </h1>
          <p className={cls.subtitle}>
            {t("memberProfileEditMember.loading", {
              defaultValue: "Loading your member details…",
            })}
          </p>
        </div>
        <div className={cls.skeleton} />
      </div>
    );
  }

  /* ------------------------------- Not found UI ----------------------------- */

  if (!member) {
    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← {t("memberProfileEditMember.back", { defaultValue: "Back" })}
          </button>
          <h1 className={cls.title}>
            {t("memberProfileEditMember.title", {
              defaultValue: "Member core details",
            })}
          </h1>
          <p className={cls.subtitle}>
            {t("memberProfileEditMember.notFound", {
              defaultValue: "We couldn't find your member profile.",
            })}
          </p>
        </div>
      </div>
    );
  }

  /* --------------------------------- Main UI -------------------------------- */

  return (
    <div className={cls.page}>
      <div className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← {t("memberProfileEditMember.back", { defaultValue: "Back" })}
        </button>
        <h1 className={cls.title}>
          {t("memberProfileEditMember.title", {
            defaultValue: "Member core details",
          })}
        </h1>
        <p className={cls.subtitle}>
          {t("memberProfileEditMember.subtitle", {
            defaultValue:
              "Update your avatar, experience level, and language preferences.",
          })}
        </p>
      </div>

      <div className={cls.layout}>
        {/* LEFT COLUMN – AVATAR */}
        <section className={cls.avatarCard}>
          <div className={cls.avatarHeader}>
            <h2 className={cls.cardTitle}>
              {t("memberProfileEditMember.avatar.title", {
                defaultValue: "Your avatar",
              })}
            </h2>
            <p className={cls.cardSubtitle}>
              {t("memberProfileEditMember.avatar.subtitle", {
                defaultValue:
                  "This private avatar is used around the club experience.",
              })}
            </p>
          </div>

          <div className={cls.avatarPreviewWrap}>
            <div className={cls.avatarGlow}>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={t("memberProfileEditMember.avatar.alt", {
                    defaultValue: "Member avatar",
                  })}
                  className={cls.avatarImage}
                />
              ) : (
                <div className={cls.avatarFallback}>
                  {t("memberProfileEditMember.avatar.noAvatar", {
                    defaultValue: "No avatar yet",
                  })}
                </div>
              )}
            </div>
            {member.avatar?.updatedAt && (
              <p className={cls.avatarMeta}>
                {t("memberProfileEditMember.avatar.lastUpdatedPrefix", {
                  defaultValue: "Last updated",
                })}{" "}
                {new Date(member.avatar.updatedAt).toLocaleString()}
              </p>
            )}
          </div>

          <div className={cls.avatarForm}>
            <label className={cls.fileLabel}>
              <span className={cls.fileLabelText}>
                {t("memberProfileEditMember.avatar.choose", {
                  defaultValue: "Choose new avatar",
                })}
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarFileChange}
              />
            </label>

            {avatarFile && (
              <p className={cls.fileName}>
                {t("memberProfileEditMember.avatar.selected", {
                  defaultValue: "Selected:",
                })}{" "}
                <strong>{avatarFile.name}</strong>
              </p>
            )}

            <button
              type="button"
              className={cls.avatarBtn}
              onClick={handleUploadAvatar}
              disabled={uploading || !avatarFile}
            >
              {uploading
                ? t("memberProfileEditMember.avatar.uploading", {
                    defaultValue: "Uploading…",
                  })
                : t("memberProfileEditMember.avatar.upload", {
                    defaultValue: "Upload avatar",
                  })}
            </button>
          </div>
        </section>

        {/* RIGHT COLUMN – FORM */}
        <section className={cls.formCard}>
          <div className={cls.formHeader}>
            <h2 className={cls.cardTitle}>
              {t("memberProfileEditMember.form.heading", {
                defaultValue: "Membership settings",
              })}
            </h2>
            <p className={cls.cardSubtitle}>
              {t("memberProfileEditMember.form.description", {
                defaultValue:
                  "Adjust your experience level and secondary language. Other fields are managed by the club team.",
              })}
            </p>
          </div>

          <form className={cls.form} onSubmit={handleSave}>
            {/* Row: Experience + Status (status read-only) */}
            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="experienceLevel">
                  {t("memberProfileEditMember.form.experienceLabel", {
                    defaultValue: "Experience level",
                  })}
                </label>
                <select
                  id="experienceLevel"
                  className={cls.select}
                  value={experienceLevel}
                  onChange={(e) => setExperienceLevel(e.target.value)}
                >
                  <option value="">
                    {t("memberProfileEditMember.form.experiencePlaceholder", {
                      defaultValue: "Select experience",
                    })}
                  </option>
                  {EXPERIENCE_LEVELS.map((lvl) => (
                    <option key={lvl} value={lvl}>
                      {formatLabel(lvl)}
                    </option>
                  ))}
                </select>
              </div>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="secondaryLanguage">
                  {t("memberProfileEditMember.form.secondaryLanguageLabel", {
                    defaultValue: "Secondary language",
                  })}
                </label>
                <select
                  id="secondaryLanguage"
                  className={cls.select}
                  value={secondaryLanguage}
                  onChange={(e) => setSecondaryLanguage(e.target.value)}
                >
                  <option value="">
                    {t(
                      "memberProfileEditMember.form.secondaryLanguagePlaceholder",
                      {
                        defaultValue: "None / default",
                      },
                    )}
                  </option>
                  {SECONDARY_LANGUAGES.map((lng) => (
                    <option key={lng} value={lng}>
                      {languageLabel(lng)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row: Language + Type (type read-only) */}
            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="memberType">
                  {t("memberProfileEditMember.form.memberTypeLabel", {
                    defaultValue: "Member type",
                  })}
                </label>
                <select
                  id="memberType"
                  className={cls.select}
                  value={memberType}
                  disabled
                >
                  <option value="">
                    {t("memberProfileEditMember.form.memberTypePlaceholder", {
                      defaultValue: "Set when you joined",
                    })}
                  </option>
                  {MEMBER_TYPES.map((tVal) => (
                    <option key={tVal} value={tVal}>
                      {typeLabel(tVal)}
                    </option>
                  ))}
                </select>
              </div>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="memberRenewDay">
                  {t("memberProfileEditMember.form.renewalLabel", {
                    defaultValue: "Renewal day",
                  })}
                </label>
                <input
                  id="memberRenewDay"
                  type="date"
                  className={cls.input}
                  value={memberRenewDay}
                  disabled
                  onChange={() => {}}
                />
                <p className={cls.help}>
                  {t("memberProfileEditMember.form.renewalHelp", {
                    defaultValue:
                      "Set by the club team for membership tracking.",
                  })}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className={cls.actions}>
              <button
                type="button"
                className={cls.secondaryBtn}
                onClick={loadMember}
                disabled={saving}
              >
                {t("memberProfileEditMember.form.resetButton", {
                  defaultValue: "Reset / Refresh",
                })}
              </button>
              <button
                type="submit"
                className={cls.primaryBtn}
                disabled={saving}
              >
                {saving
                  ? t("memberProfileEditMember.form.saving", {
                      defaultValue: "Saving…",
                    })
                  : t("memberProfileEditMember.form.saveButton", {
                      defaultValue: "Save changes",
                    })}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default MemberProfileEditMember;
